import * as React from 'react';
import cn from 'classnames';
import styles from './ChangeTab.module.scss';
import {
    AnimatedTabs, Card, Details, renderRow, StatusColumn, Textarea, WarningModal,
} from 'components';
import { Button, Tab } from 'react-bootstrap';
import {
    ExpertChangeRequestStatus,
    MappedExpertChangeRequestStatus,
    MappedExpertChangeRequestType
} from 'components/enums';
import { Color, ColorStatus, formatPhoneNumber } from '_helpers';
import { ReactNodeValue } from 'components/Details';
import { StepStatus } from 'components/StepStatus';
import { ExpertChangeRequestDetails, IApiError, IExpert } from '../../../api/ApiClient';
import { useLoading } from 'hooks';
import { Dispatch } from '../../../redux/store/configureStore';
import { connect } from 'react-redux';

/** SMS revert: remove this constant and restore SmsSignModal + sendCode flow; pass real code from modal */
const EXPERT_CHANGE_REQUEST_SMS_CODE_PLACEHOLDER = '0000';

const InfoModal: React.FC<IProps> = (props) => {
    const [showReject, setShowReject] = React.useState(false);
    const [rejectReason, setRejectReason] = React.useState<string>('');

    const [approveExpertChangeRequestProcessing, approveExpertChangeRequest] = useLoading(async (code: string, captcha?: string) => props.approveExpertChangeRequest({ expertChangeRequestId: props.details.id, body: { captcha, code } }));
    const [rejectExpertChangeRequestProcessing, rejectExpertChangeRequest] = useLoading(async (code: string, captcha?: string) => props.rejectExpertChangeRequest({ expertChangeRequestId: props.details.id, body: { captcha, code, rejectReason } }), [rejectReason]);

    const handleStartToApproveExpertChangeRequest = () => {
        approveExpertChangeRequest(EXPERT_CHANGE_REQUEST_SMS_CODE_PLACEHOLDER).then((success) => {
            if (success) {
                props.onHide();
            }
        });
    };

    const handleStartToRejectExpertChangeRequest = () => {
        rejectExpertChangeRequest(EXPERT_CHANGE_REQUEST_SMS_CODE_PLACEHOLDER).then((success) => {
            if (success) {
                setShowReject(false);
                setRejectReason('');
                props.onHide();
            }
        });
    };

    const handleHideRejectModal = () => {
        setShowReject(false);
        setRejectReason('');
    };

    const getStatus = (isRejected: boolean, isSuccess: boolean) => {
        if (isRejected) {
            return 'error';
        }

        if (isSuccess) {
            return 'success';
        }

        return 'loading';
    };

    const infoHeaderButtons = () => (
      <div className="d-flex">
          <Button
            className="mr-40"
            disabled={approveExpertChangeRequestProcessing}
            onClick={handleStartToApproveExpertChangeRequest}
          >
              Утвердить запрос
          </Button>
          <Button variant="outline-secondary" onClick={() => setShowReject(true)}>
              Отклонить
          </Button>
      </div>
    );

    return (
      <WarningModal
        dialogClassName={cn('p-0', styles.p0Child)}
        titleClassNames={styles.buttonMargin}
        bodyClassNames="p-0"
        title={(props.details && props.details.fullName) || ''}
        withoutTitleMargin
        withoutTitlePadding
        withoutCancelButton
        show={props.show}
        onHide={props.onHide}
        large
      >
          <AnimatedTabs>
              <Tab title="Информация" eventKey="info">
                  <Card
                    title="Информация о запросе"
                    category="Общая информация о запросе на изменение"
                    withoutBorder
                    showLoader={props.fetchExpertChangeRequestProcessing}
                    headerChild={props.details.approvalProgress.find(ap => !ap.expertId) && infoHeaderButtons()}
                  >
                      {props.details && (
                        <section>

                            {renderRow('ДАТА СОЗДАНИЯ', props.details.dateTime ? props.details.dateTime.format('DD.MM.YYYY') : '')}
                            {renderRow('ТИП ЗАПРОСА', props.details.requestType ? MappedExpertChangeRequestType[props.details.requestType].text : '')}
                            {props.details.newExpertStatus && renderRow('НОВАЯ РОЛЬ', props.details.newExpertStatus)}
                            {renderRow(
                              'СТАТУС ЗАПРОСА',
                              <StatusColumn
                                status={props.details.status ? MappedExpertChangeRequestStatus[props.details.status] : new ColorStatus(Color.Default, '')}
                              />,
                            )}
                            {props.details.status  === ExpertChangeRequestStatus.Rejected && renderRow('ПРИЧИНА', <i>{props.details.rejectReason}</i>)}

                            {renderRow('ФАМИЛИЯ', props.details.expert.lastName, 'mt-5')}
                            {renderRow('ИМЯ', props.details.expert.firstName)}
                            {renderRow('ОТЧЕСТВО', props.details.expert.middleName)}

                            {renderRow('ДАТА РОЖДЕНИЯ', props.details.expert.birthDate.format('DD.MM.YYYY'), 'mt-5')}
                            {renderRow('РЕГИОН', props.details.expert.region)}

                            {renderRow('ТЕЛЕФОН', formatPhoneNumber(props.details.expert.phone), 'mt-5')}
                            {renderRow('ПОЧТА', props.details.expert.email)}

                            {renderRow('МЕСТО РАБОТЫ', props.details.expert.placeOfWork, 'mt-5')}
                            {renderRow('ДОЛЖНОСТЬ', props.details.expert.position)}

                        </section>
                      )}
                  </Card>
              </Tab>
              <Tab title="Процесс утверждения" eventKey="process">
                  <Card
                    title="Процесс утверждения запроса"
                    category="Процесс утверждения запроса на изменение"
                    withoutBorder
                    showLoader={props.fetchExpertChangeRequestProcessing}
                  >
                      <Details
                          centeredRow
                          withDarkRowLabel
                          infoRows={props.details ? props.details.approvalProgress.map((step, index) => {
                              if(!props.details) return new ReactNodeValue('','');
                              const prevStepCompletionDateTime = index > 0 ? props.details.approvalProgress[index - 1].completionDateTime : null;
                              const lastCompleted = (index === props.details.approvalProgress.length - 1 || props.details.approvalProgress[index + 1].completionDateTime == null) && step.completionDateTime != null;
                              return new ReactNodeValue(
                                  step.completionDateTime
                                      ? step.completionDateTime.format('DD.MM.YYYY - HH:mm')
                                      : prevStepCompletionDateTime ? 'Ожидание' : '',
                                  <StepStatus
                                      step={{
                                          value: step.value,
                                          label: step.label,
                                          status: getStatus(props.details.status === 'rejected' && lastCompleted, !!step.completionDateTime),
                                      }}
                                  />,
                              )
                          }).slice().reverse() : []}
                      />
                  </Card>
              </Tab>
          </AnimatedTabs>

          <WarningModal
            title="Отклонение запроса на изменение"
            show={showReject}
            onHide={handleHideRejectModal}
            accessBtnTittle="Отклонить запрос на изменение"
            onAccess={handleStartToRejectExpertChangeRequest}
            loading={rejectExpertChangeRequestProcessing}
          >
              <p className="mb-50">
                  Опишите причину отклонения запроса, чтобы другие члены комиссии могли ознакомиться с этой информацией.
              </p>

              <Textarea
                value={rejectReason}
                name="rejectReason"
                onChange={setRejectReason}
                rows={6}
              />
          </WarningModal>
      </WarningModal>
    );
};

interface OwnProps {
    show: boolean;
    onHide: () => void;
    details: ExpertChangeRequestDetails;
    profile: IExpert;
    fetchExpertChangeRequestProcessing?: boolean;
    apiError?: IApiError | null;
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
    approveExpertChangeRequest: dispatch.expertChanges.approveExpertChangeRequestAsync,
    rejectExpertChangeRequest: dispatch.expertChanges.rejectExpertChangeRequest,
});

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type IProps = OwnProps & DispatchProps;

export default connect(null, mapDispatchToProps)(InfoModal);
